import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LeadCaptureForm } from "@/components/LeadCaptureForm";

const resourceCategories = [
  {
    id: "samples",
    label: "Sample Datasets",
    description: "Request access to sample datasets to evaluate data quality and structure before committing.",
    items: [
      { title: "DrishtiTable — Table Structure Recognition Sample", meta: "Image + HTML · 1,421 tables", download: true },
      { title: "NULLSPACE — JEE Failure-Only Benchmark Sample", meta: "JSON · 267 questions", download: true },
      { title: "Nalanda Image VL — Science Diagram MCQ Sample", meta: "Image + MCQ · 162 questions", download: true },
    ],
  },
  {
    id: "reports",
    label: "Benchmark Reports",
    description: "Detailed reports on how our datasets impact model performance across our published benchmarks.",
    items: [
      { title: "BLINDSPOT — Frontier Model Biology Diagram Analysis", meta: "Report · 9 models · 4 conditions", download: true },
      { title: "DrishtiTable vs GPT-4o — Table Recognition Results", meta: "Report · +12.1 TEDS points", download: true },
      { title: "NalandaJEENEET — JEE/NEET Reasoning Benchmark Results", meta: "Report · 785 held-out MCQs", download: true },
    ],
  },
  {
    id: "papers",
    label: "Research & Findings",
    description: "Published findings, technical write-ups, and methodology notes from our research team.",
    items: [
      { title: "7B Beats 70B on Indian STEM Reasoning", meta: "Article", link: "/blog/7b-beats-70b-indian-stem" },
      { title: "DrishtiTable: 8B Model Tops All Cloud Models on Table Extraction", meta: "Article", link: "/blog/drishtitable-8b-beats-all-frontier-models" },
      { title: "NULLSPACE: JEE Benchmark Contamination Tracking", meta: "Article", link: "/blog/nullspace-jee-benchmark-contamination-tracking" },
    ],
  },
  {
    id: "blog",
    label: "Blog",
    description: "Results, methodology, and insights from our data and benchmarking work.",
    items: [
      { title: "BLINDSPOT — No-Image Control for Biology Diagrams", meta: "Article", link: "/blog/blindspot-no-image-control-biology-diagrams" },
      { title: "Nalanda Image VL — STEM Diagram Fine-tuning", meta: "Article", link: "/blog/nalanda-image-vl-stem-diagram-finetuning" },
      { title: "NalandaJEENEET Benchmark Launch", meta: "Article", link: "/blog/nalanda-jee-neet-bench-launch" },
    ],
  },
];

export default function ResourcesPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);

  return (
    <div className="bg-[#0A0A0A]" style={{ paddingTop: "96px" }}>
      <Helmet>
        <title>Resources — Nalandadata.ai</title>
        <meta name="description" content="Sample datasets, benchmark reports, research papers, and insights to help you evaluate and use Nalandadata AI training data." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nalandadata.ai/resources" />
        <meta property="og:title" content="Resources — Nalandadata.ai" />
        <meta property="og:description" content="Sample datasets, benchmark reports, research papers, and insights to help you evaluate and use Nalandadata AI training data." />
        <meta property="og:image" content="https://nalandadata.ai/og-social.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Resources — Nalandadata.ai" />
        <meta name="twitter:description" content="Sample datasets, benchmark reports, research papers, and insights to help you evaluate and use Nalandadata AI training data." />
        <meta name="twitter:image" content="https://nalandadata.ai/og-social.png" />
      </Helmet>

      {/* Hero */}
      <section className="s-band" style={{ borderTop: "none", paddingBottom: "40px" }}>
        <div className="s-wrap">
          <nav className="s-crumb" aria-label="Breadcrumb" style={{ marginBottom: "18px" }}>
            <Link to="/">Home</Link> / Resources
          </nav>
          <p className="s-eyebrow">Resources</p>
          <h1 style={{ fontSize: "clamp(32px,4vw,52px)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.08, margin: "12px 0 16px", color: "var(--paper)" }}>
            Resources
          </h1>
          <p style={{ color: "var(--muted)", fontSize: "16px", lineHeight: 1.6, maxWidth: "52ch" }}>
            Sample datasets, benchmark reports, research papers, and insights to help you evaluate and use our data.
          </p>
        </div>
      </section>

      {/* Resource cards */}
      <section className="s-band alt">
        <div className="s-wrap">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: "20px" }}>
            {resourceCategories.map((category) => (
              <div key={category.id} style={{ background: "var(--ink)", border: "1px solid var(--line)", borderRadius: "12px", padding: "28px" }}>
                <p style={{ color: "var(--accent)", fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 8px" }}>
                  {category.label}
                </p>
                <p style={{ color: "var(--muted)", fontSize: "13.5px", lineHeight: 1.6, margin: "0 0 20px" }}>
                  {category.description}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {category.items.map((item, j) =>
                    item.link ? (
                      <Link
                        key={j}
                        to={item.link}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "var(--ink-2)", border: "1px solid var(--line)", borderRadius: "7px", textDecoration: "none", transition: "border-color 0.15s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(200,169,110,0.4)")}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--line)")}
                      >
                        <span style={{ color: "var(--paper)", fontSize: "13.5px" }}>{item.title}</span>
                        <span style={{ color: "var(--muted)", fontFamily: "var(--mono)", fontSize: "11px", flexShrink: 0, marginLeft: "12px" }}>{item.meta} ↗</span>
                      </Link>
                    ) : (
                      <button
                        key={j}
                        onClick={() => setIsFormOpen(true)}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "var(--ink-2)", border: "1px solid var(--line)", borderRadius: "7px", cursor: "pointer", width: "100%", textAlign: "left", transition: "border-color 0.15s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(200,169,110,0.4)")}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--line)")}
                      >
                        <span style={{ color: "var(--paper)", fontSize: "13.5px" }}>{item.title}</span>
                        <span style={{ color: "var(--muted)", fontFamily: "var(--mono)", fontSize: "11px", flexShrink: 0, marginLeft: "12px" }}>{item.meta} ↓</span>
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="s-band" style={{ borderTop: "1px solid var(--line)" }}>
        <div className="s-wrap" style={{ maxWidth: "600px", textAlign: "center" }}>
          <p className="s-eyebrow">Get in touch</p>
          <h2 style={{ fontSize: "clamp(24px,3vw,38px)", fontWeight: 800, letterSpacing: "-0.03em", color: "var(--paper)", margin: "12px 0 14px" }}>
            Need More Information?
          </h2>
          <p style={{ color: "var(--muted)", fontSize: "15px", lineHeight: 1.6, margin: "0 0 32px" }}>
            Our team is ready to provide additional documentation and answer any questions.
          </p>
          <button onClick={() => setIsFormOpen(true)} className="s-btn primary">
            Contact us →
          </button>
        </div>
      </section>

      <LeadCaptureForm open={isFormOpen} onOpenChange={setIsFormOpen} />
    </div>
  );
}
